/**
 * One lead: who they are, where they stand, the timeline of every call, email and form post, and the
 * conversion that turns a won lead into a customer.
 *
 * Status moves through its own buttons rather than the edit form, because every move is written to
 * the timeline - "who moved this to Lost, and why" is the question a pipeline review asks. Logging a
 * call can move the follow-up day in the same step, which is how a follow-up list stays honest.
 */
import { TextArea, type Container } from 'webwin';
import * as crm from '../api/crm.js';
import { describeError } from '../api/http.js';
import * as marketing from '../api/marketing.js';
import { LeadActivityKind, LeadStatus, type LeadActivityDto, type LeadDetailDto } from '../contracts.gen.js';
import { join, localDateTime, money } from '../format.js';
import { activityLabel, followUpLine, isOpen, LEAD_STATUSES, leadName, LOGGABLE_ACTIVITIES, statusLabel, statusTone } from '../leads.js';
import { middayUtcFromIso, todayIso } from '../recurrence.js';
import type { Card } from '../ui/Card.js';
import { CardList } from '../ui/CardList.js';
import { chip } from '../ui/Chip.js';
import { dateBox } from '../ui/DateBox.js';
import { ask, confirm } from '../ui/dialogs.js';
import { EmptyState } from '../ui/EmptyState.js';
import { link } from '../ui/Link.js';
import { moneyBox } from '../ui/MoneyBox.js';
import { StatusLine } from '../ui/StatusLine.js';
import {
  button,
  buttonRow,
  card,
  checkBox,
  comboBox,
  field,
  fieldRow,
  fillWith,
  FONT,
  GAP,
  h1,
  h2,
  inlineRow,
  muted,
  relayout,
  scrollPage,
  splitRow,
  stackOf,
  text,
  textBox,
  value,
  vstack
} from '../ui/layout.js';
import { leadEditor } from './sections/lead-editor.js';
import type { CrmPage, PageContext } from './page.js';

/** Activities a person wrote, which can be removed again; the status trail cannot. */
const REMOVABLE = new Set<LeadActivityKind>(LOGGABLE_ACTIVITIES);

export function leadDetailPage(): CrmPage {
  return {
    async mount(host: Container, ctx: PageContext) {
      const id = Number(ctx.params.id);
      let detail: LeadDetailDto | null = null;
      let busy = false;

      const status = new StatusLine();
      const title = h1('Lead');
      const titleChips = inlineRow();
      const titleRow = splitRow({ align: 'center' });
      fillWith(titleRow, stackOf(vstack({ spacing: GAP.xs }), title, titleChips));
      const header = stackOf(
        vstack({ spacing: GAP.sm }),
        link('← All leads', () => ctx.go('/leads'), 'brand'),
        titleRow,
        stackOf(buttonRow(), button('Edit', () => detail && editor.open(detail.lead)), button('Delete', () => void remove())),
        status.label
      );

      const editor = leadEditor({
        cancelled: () => ctx.cancelled(),
        onSaved: () => {
          status.ok('Saved.');
          void load();
        }
      });

      // ---- details ----
      const details = stackOf(vstack({ spacing: GAP.xs }));
      const detailsCard = card();
      detailsCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), h2('Details'), details));

      // ---- status ----
      const statusButtons = buttonRow();
      const statusLine = new StatusLine();
      const statusNote = muted('');
      const statusCard = card();
      statusCard.controls.add(
        stackOf(vstack({ spacing: GAP.sm }), h2('Pipeline'), statusNote, statusButtons, statusLine.label)
      );

      // ---- conversion ----
      const rate = moneyBox();
      const terms = textBox({ text: 'Net 30' });
      const asEmployee = checkBox('Add the contact as the customer’s first employee', true);
      const convertLine = new StatusLine();
      const convertBody = stackOf(
        vstack({ spacing: GAP.md }),
        muted('Creates the customer from this lead’s company, contact and address, marks the lead Won, and links the two.'),
        fieldRow(field('Default hourly rate', rate.row), field('Billing terms', terms)),
        asEmployee,
        stackOf(buttonRow(), button('Convert to customer', () => void convert(), true))
      );
      const convertedBody = stackOf(vstack({ spacing: GAP.sm }));
      const convertCard = card();
      convertCard.controls.add(stackOf(vstack({ spacing: GAP.sm }), h2('Customer'), convertBody, convertedBody, convertLine.label));

      // ---- log an activity ----
      const kind = comboBox<string>(LOGGABLE_ACTIVITIES.map(activityLabel), { selectedIndex: 0 });
      const body = new TextArea({ height: 90, hAlign: 'stretch', wrap: true });
      const nextFollow = dateBox();
      const clearFollow = checkBox('No further follow-up', false);
      const logLine = new StatusLine();
      const logCard = card();
      logCard.controls.add(
        stackOf(
          vstack({ spacing: GAP.md }),
          h2('Log an activity'),
          fieldRow(field('What', kind), field('Next follow-up', nextFollow.picker)),
          field('What happened', body),
          clearFollow,
          stackOf(buttonRow(), button('Add to timeline', () => void addActivity(), true)),
          logLine.label
        )
      );

      // ---- timeline ----
      const timeline = new CardList<LeadActivityDto>({ key: a => a.id, build: a => activityCard(a) });
      const timelineEmpty = new EmptyState('Nothing on the timeline yet.');

      const { view, content } = scrollPage();
      content.controls.add(
        stackOf(
          vstack({ spacing: GAP.lg }),
          header,
          editor.card,
          detailsCard,
          statusCard,
          convertCard,
          logCard,
          h2('Timeline'),
          timelineEmpty.panel,
          timeline.panel
        )
      );
      host.controls.add(view);

      function activityCard(a: LeadActivityDto): Card {
        const heading = inlineRow();
        heading.controls.add(value(a.kindLabel, { font: FONT.bold }));
        heading.controls.add(value(join([localDateTime(a.whenUtc), a.actorName]), { font: FONT.small, role: 'muted' }));
        const column = stackOf(vstack({ spacing: GAP.xs }), heading, text(a.body ?? ''));
        if (REMOVABLE.has(a.kind)) column.controls.add(stackOf(buttonRow(), button('Remove', () => void removeActivity(a))));
        const system = a.kind === LeadActivityKind.StatusChange || a.kind === LeadActivityKind.Converted;
        const shell = system ? card({ tint: 'alt' }) : card();
        shell.controls.add(column);
        return shell;
      }

      function line(label: string, content: string | null | undefined): void {
        if (content) details.controls.add(text(`${label}: ${content}`));
      }

      function render(d: LeadDetailDto): void {
        detail = d;
        const l = d.lead;
        title.text = leadName(l);
        titleChips.controls.clear();
        titleChips.controls.add(chip(l.statusLabel, statusTone(l.status)));
        titleChips.controls.add(chip(l.sourceLabel, 'neutral'));
        if (l.unsubscribed) titleChips.controls.add(chip('unsubscribed from marketing', 'warn'));
        for (const t of l.tags) titleChips.controls.add(chip(t, 'neutral'));

        details.controls.clear();
        line('Contact', l.company ? l.contactName : null);
        line('Email', l.email);
        line('Phone', l.phone);
        line('Website', l.website);
        line('Address', l.address);
        line('Source', join([l.sourceLabel, l.sourceDetail]));
        line('Worth', l.estimatedMonthlyValue > 0 ? `${money(l.estimatedMonthlyValue)} a month` : null);
        line('Owner', l.ownerName);
        const follow = isOpen(l.status) ? followUpLine(l.nextFollowUpUtc, todayIso()) : null;
        if (follow) details.controls.add(muted(follow, l.isFollowUpDue ? 'warn' : 'muted'));
        if (l.notes) details.controls.add(text(l.notes));
        details.controls.add(muted(`Added ${localDateTime(l.createdUtc)} · last activity ${localDateTime(l.lastActivityUtc)}`));

        statusNote.text =
          l.status === LeadStatus.Lost && l.lostReason
            ? `Lost: ${l.lostReason}`
            : l.customerId !== null
              ? 'This lead became a customer, so it stays Won.'
              : 'Move the lead along as it progresses. Every move is written to the timeline.';
        statusButtons.controls.clear();
        if (l.customerId === null) {
          for (const s of LEAD_STATUSES) {
            if (s === l.status) continue;
            statusButtons.controls.add(button(statusLabel(s), () => void setStatus(s), s === LeadStatus.Won));
          }
        }

        const converted = l.customerId !== null;
        convertBody.visible = !converted;
        convertedBody.visible = converted;
        convertedBody.controls.clear();
        if (converted) {
          convertedBody.controls.add(text(`Became customer ${l.customerName || '#' + l.customerId}.`));
          convertedBody.controls.add(stackOf(buttonRow(), button('Open customer', () => ctx.go(`/customers/${l.customerId}`), true)));
        }

        timeline.set(d.activities);
        timelineEmpty.update(d.activities.length);
        relayout(content);
      }

      async function load(): Promise<void> {
        try {
          const d = await marketing.getLead(id, ctx.dispose.signal());
          if (ctx.cancelled()) return;
          render(d);
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        }
      }

      async function setStatus(next: LeadStatus): Promise<void> {
        if (busy || !detail) return;
        let reason: string | null = null;
        if (next === LeadStatus.Lost) {
          reason = await ask(ctx.desktop, {
            title: 'Mark lost',
            prompt: 'Why was it lost? (optional - price, went with someone else, no budget…)',
            okLabel: 'Mark lost'
          });
          if (reason === null || ctx.cancelled()) return;
        }
        busy = true;
        statusLine.busy('Saving…');
        try {
          await marketing.setLeadStatus(id, { status: next, lostReason: reason });
          if (ctx.cancelled()) return;
          statusLine.ok(`Moved to ${statusLabel(next)}.`);
          await load();
        } catch (error) {
          if (!ctx.cancelled()) statusLine.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function convert(): Promise<void> {
        if (busy || !detail) return;
        const r = rate.value();
        if (r !== null && r < 0) {
          convertLine.error('The hourly rate cannot be negative.');
          return;
        }
        const ok = await confirm(ctx.desktop, {
          title: 'Convert to customer',
          message: `Create a customer named "${leadName(detail.lead)}" from this lead and mark it Won?`
        });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        convertLine.busy('Creating the customer…');
        try {
          await marketing.convertLead(id, {
            defaultRate: r ?? 0,
            billingTerms: terms.text.trim(),
            addContactAsEmployee: asEmployee.checked
          });
          if (ctx.cancelled()) return;
          convertLine.ok('Created the customer.');
          await load();
        } catch (error) {
          if (!ctx.cancelled()) convertLine.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function addActivity(): Promise<void> {
        if (busy) return;
        const what = body.text.trim();
        if (!what) {
          logLine.error('Write what happened.');
          return;
        }
        busy = true;
        logLine.busy('Adding…');
        try {
          const d = await marketing.addLeadActivity(id, {
            kind: LOGGABLE_ACTIVITIES[kind.selectedIndex] ?? LeadActivityKind.Note,
            body: what,
            nextFollowUpUtc: middayUtcFromIso(nextFollow.iso())?.toISOString() ?? null,
            clearFollowUp: clearFollow.checked
          });
          if (ctx.cancelled()) return;
          body.text = '';
          nextFollow.set(null);
          clearFollow.checked = false;
          logLine.ok('Added to the timeline.');
          render(d);
        } catch (error) {
          if (!ctx.cancelled()) logLine.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function removeActivity(a: LeadActivityDto): Promise<void> {
        if (busy) return;
        const ok = await confirm(ctx.desktop, { title: 'Remove entry', message: `Remove this ${a.kindLabel.toLowerCase()} from the timeline?` });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        try {
          render(await marketing.deleteLeadActivity(id, a.id));
        } catch (error) {
          if (!ctx.cancelled()) logLine.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      async function remove(): Promise<void> {
        if (busy || !detail) return;
        const ok = await confirm(ctx.desktop, {
          title: 'Delete lead',
          message: `Delete ${leadName(detail.lead)} and its timeline? ${detail.lead.customerId !== null ? 'The customer it became is kept.' : 'This cannot be undone.'}`
        });
        if (!ok || ctx.cancelled()) return;
        busy = true;
        try {
          await marketing.deleteLead(id);
          ctx.go('/leads', { replace: true });
        } catch (error) {
          if (!ctx.cancelled()) status.error(describeError(error));
        } finally {
          busy = false;
        }
      }

      try {
        editor.setUsers(await crm.getStaffUsers(ctx.dispose.signal()));
      } catch {
        // The owner picker stays at "Nobody"; the lead still loads.
      }
      if (ctx.cancelled()) return;
      await load();
    }
  };
}
